import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  etiquetaEstadoChequeTercero,
  resolverEstadoChequeTercero,
} from '../../utils/chequeTerceroEstado';

/** Datos mínimos comunes a los informes de cheques de tercero / cartera */
export interface ChequeTerceroDetalleData {
  nroCarga: number;
  nroBanco: number;
  nombreBanco?: string;
  nroSucursal: number;
  nombreSucursal?: string;
  nroCheque: number;
  nroLibrador: string;
  librador: string;
  importe: number;
  fechaIngreso: string;
  nroMovCajaIngre: number | null;
  nroDestino: number | null;
  fechaSalida: string | null;
  nroMovCajaSalida: number | null;
  fechaVencimiento: string;
  concepto: string;
  fechaEmision: string;
  conciliado: number;
  asentado: number;
  mcp: string;
  nroCuenta: string;
  comprobanteIngreso?: string;
  comprobanteSalida?: string;
  senial?: number;
}

interface ChequeTerceroDetalleDialogProps {
  open: boolean;
  onClose: () => void;
  cheque: ChequeTerceroDetalleData | null;
  /** Tras guardar correctamente (solo SUPERADMIN) */
  onSaved?: () => void;
}

const toDateInput = (s: string | null | undefined): string => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const numToStr = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : String(n);

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short' }).format(date);
  } catch {
    return dateString;
  }
};

const ChequeTerceroDetalleDialog: React.FC<ChequeTerceroDetalleDialogProps> = ({
  open,
  onClose,
  cheque,
  onSaved
}) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const isSuperAdmin = Boolean(user?.roles?.includes('SUPERADMIN'));

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});

  const syncFormFromCheque = useCallback((c: ChequeTerceroDetalleData) => {
    setForm({
      nroBanco: String(c.nroBanco),
      nroSucursal: String(c.nroSucursal),
      nroCheque: String(c.nroCheque),
      nroLibrador: (c.nroLibrador ?? '').trim(),
      librador: (c.librador ?? '').trim(),
      importe: String(c.importe ?? 0),
      fechaEmision: toDateInput(c.fechaEmision),
      fechaVencimiento: toDateInput(c.fechaVencimiento),
      fechaIngreso: toDateInput(c.fechaIngreso),
      fechaSalida: toDateInput(c.fechaSalida ?? undefined),
      nroMovCajaIngre: numToStr(c.nroMovCajaIngre),
      nroMovCajaSalida: numToStr(c.nroMovCajaSalida),
      nroDestino: numToStr(c.nroDestino),
      concepto: (c.concepto ?? '').trim(),
      mcp: (c.mcp ?? '').trim(),
      nroCuenta: (c.nroCuenta ?? '').trim(),
      conciliado: String(c.conciliado ?? 0),
      asentado: String(c.asentado ?? 0),
      senial: c.senial !== undefined && c.senial !== null ? String(c.senial) : ''
    });
  }, []);

  useEffect(() => {
    if (open && cheque) syncFormFromCheque(cheque);
  }, [open, cheque, syncFormFromCheque]);

  const getEstadoChip = (c: ChequeTerceroDetalleData) => {
    const estado = resolverEstadoChequeTercero(c);
    const color =
      estado === 'anulado'
        ? 'default'
        : estado === 'conciliado'
          ? 'success'
          : estado === 'entregado'
            ? 'warning'
            : 'info';
    return (
      <Chip label={etiquetaEstadoChequeTercero(estado)} color={color} size="small" />
    );
  };

  const parseNullableInt = (s: string): number | null => {
    const t = s.trim();
    if (t === '') return null;
    const n = parseInt(t, 10);
    return Number.isNaN(n) ? null : n;
  };

  const parseNullableByte = (s: string): number | null => {
    const t = s.trim();
    if (t === '') return null;
    const n = parseInt(t, 10);
    if (Number.isNaN(n) || n < 0 || n > 255) return null;
    return n;
  };

  const buildIsoDate = (yyyyMmDd: string): string | null => {
    if (!yyyyMmDd || !yyyyMmDd.trim()) return null;
    const d = new Date(`${yyyyMmDd.trim()}T12:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const handleSave = async () => {
    if (!cheque || !isSuperAdmin) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const imp = parseFloat(String(form.importe ?? '').replace(',', '.'));
      if (Number.isNaN(imp)) {
        enqueueSnackbar('Importe inválido.', { variant: 'error' });
        return;
      }
      const body = {
        nroBanco: parseInt(form.nroBanco, 10),
        nroSucursal: parseInt(form.nroSucursal, 10),
        nroCheque: parseInt(form.nroCheque, 10),
        nroLibrador: form.nroLibrador ?? '',
        librador: form.librador ?? '',
        importe: imp,
        fechaEmision: buildIsoDate(form.fechaEmision ?? ''),
        fechaVencimiento: buildIsoDate(form.fechaVencimiento ?? ''),
        fechaIngreso: buildIsoDate(form.fechaIngreso ?? ''),
        fechaSalida: buildIsoDate(form.fechaSalida ?? ''),
        nroMovCajaIngre: parseNullableInt(form.nroMovCajaIngre ?? ''),
        nroMovCajaSalida: parseNullableInt(form.nroMovCajaSalida ?? ''),
        nroDestino: parseNullableInt(form.nroDestino ?? ''),
        concepto: form.concepto ?? '',
        mcp: form.mcp ?? '',
        nroCuenta: form.nroCuenta ?? '',
        conciliado: parseNullableByte(form.conciliado ?? ''),
        asentado: parseNullableByte(form.asentado ?? ''),
        senial: parseNullableByte(form.senial ?? '')
      };

      if ([body.nroBanco, body.nroSucursal, body.nroCheque].some((x) => Number.isNaN(x))) {
        enqueueSnackbar('Banco, sucursal o nº de cheque inválidos.', { variant: 'error' });
        return;
      }

      await api.put(`/api/Tesoreria/cheques-terceros/${cheque.nroCarga}`, body, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      });
      enqueueSnackbar('Cheque actualizado correctamente.', { variant: 'success' });
      onSaved?.();
      onClose();
    } catch (err: any) {
      const msg =
        err.response?.data?.message ??
        err.response?.data?.title ??
        err.message ??
        'No se pudo guardar el cheque.';
      enqueueSnackbar(typeof msg === 'string' ? msg : 'Error al guardar.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleField = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  if (!cheque) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Detalle del Cheque{isSuperAdmin ? ' (edición)' : ''}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 0 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Carga</Typography>
            <Typography variant="body1">{cheque.nroCarga || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Cheque</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroCheque ?? ''} onChange={handleField('nroCheque')} />
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{cheque.nroCheque || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Banco (código)</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroBanco ?? ''} onChange={handleField('nroBanco')} />
            ) : (
              <Typography variant="body1">{cheque.nombreBanco?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Sucursal (código)</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroSucursal ?? ''} onChange={handleField('nroSucursal')} />
            ) : (
              <Typography variant="body1">{cheque.nombreSucursal?.trim() || '-'}</Typography>
            )}
          </Grid>
          {!isSuperAdmin && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Nombre banco</Typography>
                <Typography variant="body1">{cheque.nombreBanco?.trim() || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Nombre sucursal</Typography>
                <Typography variant="body1">{cheque.nombreSucursal?.trim() || '-'}</Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Librador</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.librador ?? ''} onChange={handleField('librador')} />
            ) : (
              <Typography variant="body1">{cheque.librador?.trim() || cheque.nroLibrador?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Librador</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroLibrador ?? ''} onChange={handleField('nroLibrador')} />
            ) : (
              <Typography variant="body1">{cheque.nroLibrador?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Importe</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.importe ?? ''} onChange={handleField('importe')} />
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatCurrency(cheque.importe || 0)}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Cuenta</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroCuenta ?? ''} onChange={handleField('nroCuenta')} />
            ) : (
              <Typography variant="body1">{cheque.nroCuenta?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Fecha Emisión</Typography>
            {isSuperAdmin ? (
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fechaEmision ?? ''} onChange={handleField('fechaEmision')} />
            ) : (
              <Typography variant="body1">{formatDate(cheque.fechaEmision)}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Fecha Vencimiento</Typography>
            {isSuperAdmin ? (
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fechaVencimiento ?? ''} onChange={handleField('fechaVencimiento')} />
            ) : (
              <Typography variant="body1">{formatDate(cheque.fechaVencimiento)}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Fecha Ingreso</Typography>
            {isSuperAdmin ? (
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fechaIngreso ?? ''} onChange={handleField('fechaIngreso')} />
            ) : (
              <Typography variant="body1">{formatDate(cheque.fechaIngreso)}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Fecha Salida</Typography>
            {isSuperAdmin ? (
              <TextField type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fechaSalida ?? ''} onChange={handleField('fechaSalida')} />
            ) : (
              <Typography variant="body1">{formatDate(cheque.fechaSalida || '')}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Mov. Caja Ingreso</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroMovCajaIngre ?? ''} onChange={handleField('nroMovCajaIngre')} />
            ) : (
              <Typography variant="body1">{cheque.nroMovCajaIngre ?? '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Mov. Caja Salida</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroMovCajaSalida ?? ''} onChange={handleField('nroMovCajaSalida')} />
            ) : (
              <Typography variant="body1">{cheque.nroMovCajaSalida ?? '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Comprobante Ingreso</Typography>
            <Typography variant="body1">{cheque.comprobanteIngreso?.trim() || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Comprobante Salida</Typography>
            <Typography variant="body1">{cheque.comprobanteSalida?.trim() || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Nro. Destino (Caja)</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.nroDestino ?? ''} onChange={handleField('nroDestino')} />
            ) : (
              <Typography variant="body1">{cheque.nroDestino ?? '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">MCP</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.mcp ?? ''} onChange={handleField('mcp')} />
            ) : (
              <Typography variant="body1">{cheque.mcp?.trim() || '-'}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Estado</Typography>
            <Box sx={{ mt: 0.5 }}>{getEstadoChip(cheque)}</Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Conciliado (0/1)</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth size="small" value={form.conciliado ?? ''} onChange={handleField('conciliado')} />
            ) : (
              <Typography variant="body1">{cheque.conciliado ? 'Sí' : 'No'}</Typography>
            )}
          </Grid>
          {isSuperAdmin && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Asentado (0/1)</Typography>
                <TextField fullWidth size="small" value={form.asentado ?? ''} onChange={handleField('asentado')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Señal</Typography>
                <TextField fullWidth size="small" value={form.senial ?? ''} onChange={handleField('senial')} />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Concepto</Typography>
            {isSuperAdmin ? (
              <TextField fullWidth multiline minRows={2} size="small" value={form.concepto ?? ''} onChange={handleField('concepto')} />
            ) : (
              <Typography variant="body1">{cheque.concepto?.trim() || '-'}</Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cerrar</Button>
        {isSuperAdmin && (
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}>
            Guardar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChequeTerceroDetalleDialog;
